import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { Typography, Drawer, DropdownMenu, DropdownMenuItem, TextButton } from 'seagull';
import { ChevronDown, ChevronUp } from 'seagull/icons';
import { useStoreActions } from '~/contexts';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { useOpenMapServicesModal } from '~/integrations/Accounting/MapServicesModal/useOpenMapServicesModal';
import { Accounting } from '~/types/api.model';
import { getUnusedTimeActivities, handleAddAllTimeActivities, handleAddSingleTimeActivity } from '../BillClientModal.utils';
import { TimeActivityActions } from '../TimeActivityActions/TimeActivityActions';
import { TimeActivityCard } from '../TimeActivityCard/TimeActivityCard';
import styles from './TimeActivitiesDrawer.module.scss';
const TimeActivitiesDrawer = (props)=>{
    const { open, itemsControl, serviceOptions, timeActivities, relationship, setRelationship, close, getValues } = props;
    const [selectedService, setSelectedService] = useState(serviceOptions[0]);
    const [servicesSelectorOpen, setServicesSelectorOpen] = useState(false);
    const { fetchQboTimeActivities } = useStoreActions();
    const handleTimeActivitiesServiceSelected = (selectedOption)=>{
        setSelectedService(selectedOption);
        setServicesSelectorOpen(false);
    };
    const fullSelectedService = useMemo(()=>relationship.services.find((s)=>s.id === selectedService.id), [
        relationship,
        selectedService
    ]);
    const selectedServiceTimeActivities = useMemo(()=>timeActivities.getTimeActivitiesForService(selectedService.id), [
        timeActivities,
        selectedService
    ]);
    const { fullName: accountingName } = getAccountingDetails(Accounting.QBO);
    const items = getValues('items');
    const unusedTimeActivities = useMemo(()=>getUnusedTimeActivities(items, selectedServiceTimeActivities), [
        items,
        selectedServiceTimeActivities
    ]);
    return /*#__PURE__*/ _jsx(Drawer, {
        PaperProps: {
            style: {
                position: 'absolute',
                width: 680
            }
        },
        variant: "persistent",
        open: open,
        headerClassName: styles.drawerHeader,
        header: /*#__PURE__*/ _jsxs("div", {
            className: styles.timeActivitiesHeader,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    weight: "normal",
                    children: "Billable Time"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu, {
                    hAlign: "left",
                    maxHeight: 200,
                    onOpenChange: setServicesSelectorOpen,
                    target: /*#__PURE__*/ _jsxs("div", {
                        className: styles.timeActivitiesServicesSelector,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                weight: "normal",
                                ellipsis: true,
                                skin: "primary",
                                className: styles.timeActivitiesServiceName,
                                testId: "time-activities-service-name",
                                children: selectedService.name
                            }),
                            servicesSelectorOpen ? /*#__PURE__*/ _jsx(ChevronUp, {
                                size: 20,
                                className: styles.arrowIcon
                            }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                size: 20,
                                className: cn(styles.arrowIcon, styles.arrowIconDown)
                            })
                        ]
                    }),
                    children: serviceOptions.map((item)=>{
                        const { id, name, costString } = item;
                        const isSelected = selectedService.id === id;
                        return /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                            bi: {
                                name: 'time_activities_service_option'
                            },
                            height: 56,
                            testId: "time-activities-service-option-".concat(id),
                            onClick: ()=>handleTimeActivitiesServiceSelected(item),
                            selected: isSelected,
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    ellipsis: true,
                                    variant: "paragraph1",
                                    testId: "option-".concat(id, "-title"),
                                    children: name
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    ellipsis: true,
                                    variant: "paragraph1",
                                    skin: "secondary",
                                    children: costString
                                })
                            ]
                        }, id);
                    })
                })
            ]
        }),
        onClose: close,
        onCloseButtonClick: close,
        BackdropProps: {
            invisible: true
        },
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": "time-activities-side-panel",
            children: [
                /*#__PURE__*/ _jsx(TimeActivityActions, {
                    handleAddAllTimeActivities: ()=>{
                        if (!fullSelectedService) return;
                        handleAddAllTimeActivities({
                            itemsControl,
                            timeActivities: selectedServiceTimeActivities,
                            fullSelectedService,
                            getValues
                        });
                    },
                    showAddAll: !!(fullSelectedService === null || fullSelectedService === void 0 ? void 0 : fullSelectedService.integrations.qboIntegratedId()) && unusedTimeActivities.length > 0,
                    relationshipId: relationship.id
                }),
                (fullSelectedService === null || fullSelectedService === void 0 ? void 0 : fullSelectedService.integrations.qboIntegratedId()) ? unusedTimeActivities.length ? unusedTimeActivities.map((timeActivity)=>/*#__PURE__*/ _jsx(TimeActivityCard, {
                        timeActivity: timeActivity,
                        handleAddSingleTimeActivity: ()=>{
                            if (!fullSelectedService) return;
                            handleAddSingleTimeActivity({
                                timeActivity,
                                itemsControl,
                                fullSelectedService,
                                getValues
                            });
                        }
                    }, timeActivity.id)) : /*#__PURE__*/ _jsx("div", {
                    className: styles.timeActivitiesEmptyState,
                    children: /*#__PURE__*/ _jsxs(Typography, {
                        testId: "time-activities-empty-state",
                        variant: "paragraph1",
                        skin: "secondary",
                        children: [
                            "No billable time activities found for this service in ",
                            accountingName
                        ]
                    })
                }) : /*#__PURE__*/ _jsx("div", {
                    className: styles.timeActivitiesEmptyState,
                    children: /*#__PURE__*/ _jsxs(Typography, {
                        testId: "time-activities-empty-state",
                        variant: "paragraph1",
                        skin: "secondary",
                        children: [
                            "Service is not mapped with ".concat(accountingName, ". "),
                            fullSelectedService && /*#__PURE__*/ _jsx(MapServiceMessage, {
                                service: fullSelectedService,
                                onRefresh: async (param)=>{
                                    let { serviceTemplateId, accountingItemId } = param;
                                    const updatedServices = relationship.services.map((s)=>{
                                        if (s.serviceTemplateId === serviceTemplateId) {
                                            const integrationQBO = s.integrations.setQboIntegratedId(accountingItemId);
                                            return _object_spread_props(_object_spread({}, s), {
                                                integrations: integrationQBO
                                            });
                                        }
                                        return s;
                                    });
                                    setRelationship((old)=>_object_spread_props(_object_spread({}, old), {
                                            services: updatedServices
                                        }));
                                    await fetchQboTimeActivities(relationship.id);
                                }
                            })
                        ]
                    })
                })
            ]
        })
    });
};
export default TimeActivitiesDrawer;
const MapServiceMessage = (param)=>{
    let { service, onRefresh } = param;
    const serviceTemplateIds = getAccountingDetails(Accounting.QBO).policy.getServiceTemplateIdsFromServices([
        service
    ]);
    const openMapServicesModal = useOpenMapServicesModal(serviceTemplateIds, onRefresh);
    return /*#__PURE__*/ _jsx(TextButton, {
        testId: "map-service-button",
        underline: false,
        onClick: ()=>openMapServicesModal(Accounting.QBO),
        children: "Map service"
    });
};
